import { z } from "zod";
import { requestedCacheWrites } from "../../shared/cache-writes";
import type { ModelErrorType } from "../../shared/errors";
import {
  argumentsText,
  contentText,
  errorCode,
  finiteNumber,
  isRecord,
  jsonObject,
  jsonObjects,
  nonEmptyString,
  outputType,
  parseArguments,
  readableText,
  records,
  upstreamCost,
} from "../../shared/json-body";
import { encryptedReasoningPart, toolPart } from "../../shared/prompt";
import type {
  DeclaredTool,
  ModelPrompt,
  ModelReply,
  ModelRequestSettings,
  ModelTransportDefinition,
  OutputContract,
  PromptPart,
  StreamChunk,
} from "../../shared/transport";
import type { ModelUsage } from "../../shared/usage";
import { openrouterRefusal, openrouterRequestedFacts, openrouterSkuOf } from "./routing";

const CacheControl = z.strictObject({
  type: z.literal("ephemeral"),
  ttl: z.enum(["5m", "1h"]).optional(),
});

const TextPart = z.strictObject({
  type: z.literal("text"),
  text: z.string(),
  cache_control: CacheControl.optional(),
});

const UserPart = z.discriminatedUnion("type", [
  TextPart,
  z.strictObject({
    type: z.literal("image_url"),
    image_url: z.strictObject({
      url: z.string(),
      detail: z.enum(["auto", "low", "high"]).optional(),
    }),
    cache_control: CacheControl.optional(),
  }),
  z.strictObject({
    type: z.literal("input_audio"),
    input_audio: z.strictObject({ data: z.string(), format: z.string() }),
    cache_control: CacheControl.optional(),
  }),
  z.strictObject({
    type: z.literal("video_url"),
    video_url: z.strictObject({ url: z.string() }),
    cache_control: CacheControl.optional(),
  }),
  z.strictObject({
    type: z.literal("file"),
    file: z.strictObject({ filename: z.string().optional(), file_data: z.string() }),
    cache_control: CacheControl.optional(),
  }),
]);

const textContent = z.union([z.string(), z.array(TextPart)]);

const ToolCall = z.strictObject({
  id: z.string(),
  type: z.literal("function"),
  function: z.strictObject({ name: z.string(), arguments: z.string() }),
});

const Message = z.discriminatedUnion("role", [
  z.strictObject({
    role: z.literal("system"),
    content: textContent,
    name: z.string().optional(),
    cache_control: CacheControl.optional(),
  }),
  z.strictObject({
    role: z.literal("developer"),
    content: textContent,
    name: z.string().optional(),
    cache_control: CacheControl.optional(),
  }),
  z.strictObject({
    role: z.literal("user"),
    content: z.union([z.string(), z.array(UserPart)]),
    name: z.string().optional(),
    cache_control: CacheControl.optional(),
  }),
  z.strictObject({
    role: z.literal("assistant"),
    content: textContent.nullable().optional(),
    name: z.string().optional(),
    tool_calls: z.array(ToolCall).optional(),
    refusal: z.string().nullable().optional(),
    reasoning: z.string().nullable().optional(),
    /** Reasoning the model returned, carried back as OpenRouter sent it, signatures included. */
    reasoning_details: z.array(z.looseObject({ type: z.string() })).optional(),
    annotations: z.array(z.looseObject({ type: z.string() })).optional(),
    cache_control: CacheControl.optional(),
  }),
  z.strictObject({
    role: z.literal("tool"),
    content: textContent,
    tool_call_id: z.string(),
    name: z.string().optional(),
    cache_control: CacheControl.optional(),
  }),
]);

/** Function tools only: OpenRouter's server tools, such as web search, have no SKU. */
const Tool = z.strictObject({
  type: z.literal("function"),
  function: z.strictObject({
    name: z.string(),
    description: z.string().optional(),
    parameters: z.record(z.string(), z.unknown()).optional(),
    strict: z.boolean().nullable().optional(),
  }),
  cache_control: CacheControl.optional(),
  eager_input_streaming: z.boolean().optional(),
});

const ToolChoice = z.union([
  z.enum(["none", "auto", "required"]),
  z.strictObject({
    type: z.literal("function"),
    function: z.strictObject({ name: z.string() }),
  }),
]);

const ResponseFormat = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("text") }),
  z.strictObject({ type: z.literal("json_object") }),
  z.strictObject({
    type: z.literal("json_schema"),
    json_schema: z.strictObject({
      name: z.string(),
      schema: z.record(z.string(), z.unknown()).optional(),
      description: z.string().optional(),
      strict: z.boolean().nullable().optional(),
    }),
  }),
]);

const providerNames = z.array(z.string()).nullable().optional();

/** OpenRouter's routing preferences. None of them keeps state or changes what Pax can price. */
const ProviderPreferences = z.strictObject({
  allow_fallbacks: z.boolean().nullable().optional(),
  require_parameters: z.boolean().nullable().optional(),
  data_collection: z.enum(["allow", "deny"]).nullable().optional(),
  zdr: z.boolean().nullable().optional(),
  enforce_distillable_text: z.boolean().nullable().optional(),
  order: providerNames,
  only: providerNames,
  ignore: providerNames,
  quantizations: providerNames,
  sort: z
    .union([z.enum(["price", "throughput", "latency"]), z.looseObject({ by: z.string() })])
    .nullable()
    .optional(),
  max_price: z
    .strictObject({
      prompt: z.union([z.number(), z.string()]).optional(),
      completion: z.union([z.number(), z.string()]).optional(),
      image: z.union([z.number(), z.string()]).optional(),
      audio: z.union([z.number(), z.string()]).optional(),
      request: z.union([z.number(), z.string()]).optional(),
    })
    .optional(),
  preferred_min_throughput: z
    .union([z.number(), z.looseObject({})])
    .nullable()
    .optional(),
  preferred_max_latency: z
    .union([z.number(), z.looseObject({})])
    .nullable()
    .optional(),
});

/** Plugins that change neither price nor state: repairing JSON, and trimming a long prompt. */
const Plugin = z.discriminatedUnion("id", [
  z.strictObject({ id: z.literal("response-healing"), enabled: z.boolean().optional() }),
  z.strictObject({ id: z.literal("context-compression"), enabled: z.boolean().optional() }),
]);

export const OpenRouterChatCompletionsRequest = z
  .strictObject({
    model: z.string().min(1).meta({ example: "openai/gpt-6-luna" }),
    models: z.array(z.string()).optional().meta({
      description: "Fallback models. Every one must be a model Pax has OpenRouter SKUs for.",
    }),
    messages: z.array(Message),
    max_tokens: z.number().int().positive().optional().meta({ example: 8192 }),
    max_completion_tokens: z.number().int().positive().optional(),
    temperature: z.number().min(0).max(2).nullable().optional(),
    top_p: z.number().min(0).max(1).nullable().optional(),
    top_k: z.number().int().nonnegative().nullable().optional(),
    top_a: z.number().nullable().optional(),
    min_p: z.number().nullable().optional(),
    frequency_penalty: z.number().nullable().optional(),
    presence_penalty: z.number().nullable().optional(),
    repetition_penalty: z.number().nullable().optional(),
    seed: z.number().int().nullable().optional(),
    stop: z
      .union([z.string(), z.array(z.string())])
      .nullable()
      .optional(),
    logit_bias: z.record(z.string(), z.number()).nullable().optional(),
    logprobs: z.boolean().nullable().optional(),
    top_logprobs: z.number().int().min(0).max(20).nullable().optional(),
    stream: z.boolean().optional(),
    stream_options: z.strictObject({ include_usage: z.boolean().optional() }).nullable().optional(),
    response_format: ResponseFormat.optional(),
    tools: z.array(Tool).optional(),
    tool_choice: ToolChoice.optional(),
    parallel_tool_calls: z.boolean().nullable().optional(),
    reasoning: z
      .strictObject({
        effort: z
          .enum(["max", "xhigh", "high", "medium", "low", "minimal", "none"])
          .nullable()
          .optional(),
        max_tokens: z.number().int().positive().nullable().optional(),
        exclude: z.boolean().optional(),
        enabled: z.boolean().optional(),
        summary: z.enum(["auto", "concise", "detailed"]).nullable().optional(),
      })
      .optional(),
    reasoning_effort: z
      .enum(["max", "xhigh", "high", "medium", "low", "minimal", "none"])
      .nullable()
      .optional(),
    include_reasoning: z.boolean().optional(),
    usage: z.strictObject({ include: z.boolean() }).optional().meta({
      description: "Asks for each call's cost in `usage`, which OpenRouter always reports.",
    }),
    provider: ProviderPreferences.nullable().optional(),
    plugins: z.array(Plugin).optional(),
    cache_control: CacheControl.optional(),
    prompt_cache_key: z.string().nullable().optional(),
    prediction: z.strictObject({ type: z.literal("content"), content: z.string() }).optional(),
    metadata: z.record(z.string(), z.string()).optional(),
    session_id: z.string().optional(),
    user: z.string().optional(),
  })
  .meta({
    id: "OpenRouterChatCompletionsRequest",
    description:
      "An OpenRouter chat completions request. Every documented field is accepted except server tools, web search and plugins with no list price, file ids, and debugging options. `max_tokens` or `max_completion_tokens` is required. Any other field is refused.",
    example: {
      model: "openai/gpt-6-luna",
      max_tokens: 8192,
      messages: [{ role: "user", content: "Who holds Burgundy in 1444?" }],
    },
  });
export type OpenRouterChatCompletionsRequest = z.infer<typeof OpenRouterChatCompletionsRequest>;

export const OpenRouterChatCompletion = z
  .looseObject({
    id: z.string(),
    model: z.string(),
    choices: z.array(z.looseObject({})),
    usage: z.looseObject({}).optional(),
  })
  .meta({
    id: "OpenRouterChatCompletion",
    description:
      "OpenRouter's response, byte for byte: a chat completion, or its server-sent events when `stream` is true.",
  });

/**
 * OpenRouter's chat completions. OpenRouter reports each call's cost; Pax prices it at the SKU of
 * the endpoint that served it, so only models with OpenRouter SKUs are allowed. OpenRouter lays the prompt out for
 * whichever model serves the call, so the parts follow the request: the system messages it opens
 * with, its tools, then every other message in order.
 */
export const openrouterChatCompletions: ModelTransportDefinition<OpenRouterChatCompletionsRequest> =
  {
    vendor: "openrouter",
    provider: "openrouter",
    acceptsPath: path => path === "api/v1/chat/completions",
    upstreamUrl: () => "https://openrouter.ai/api/v1/chat/completions",
    credential: "OPENROUTER_API_KEY",
    authorize: (headers, credential, publicOrigin) => {
      headers.set("authorization", `Bearer ${credential}`);
      headers.set("http-referer", publicOrigin);
      headers.set("x-title", "Pax");
    },
    request: OpenRouterChatCompletionsRequest,
    headers: {
      "content-type": z.string().regex(/^application\/json\b/),
      accept: z.string(),
    },
    query: {},
    billedBy: "openrouter",
    readRequestedFacts: request => openrouterRequestedFacts(request.body),
    skuOf: openrouterSkuOf,
    refusal: (_path, request) =>
      openrouterRefusal(
        openrouterRequestedFacts(JSON.stringify(request)),
        request.models ?? [],
        request.max_tokens ?? request.max_completion_tokens,
      ),
    readModel: (_path, requestBody) => nonEmptyString(jsonObject(requestBody).model),
    readUsage,
    readSettings: (_path, requestBody) => readSettings(requestBody),
    readPrompt,
    readReply,
    readChunks,
    readOutputContract: (_path, requestBody) => readOutputContract(requestBody),
    errorTypeOf: openRouterErrorTypeOf,
  };

/** OpenAI's error codes, which OpenRouter passes through when OpenAI served the call. */
const openaiErrorTypes: Record<string, ModelErrorType> = {
  context_length_exceeded: "context_length_exceeded",
  rate_limit_exceeded: "rate_limited",
  insufficient_quota: "billing",
  invalid_api_key: "auth",
  model_not_found: "model_not_found",
  server_error: "server_error",
  invalid_request_error: "invalid_request",
  invalid_prompt: "invalid_request",
};

/**
 * OpenRouter's codes are HTTP statuses. A 429 is OpenRouter's own limit on Pax's key unless it
 * says the provider behind it was rate-limited, which is the provider running short, not Pax.
 */
function openRouterErrorTypeOf(
  code: string | undefined,
  message: string | undefined,
): ModelErrorType | undefined {
  if (code === "429")
    return message && /upstream|temporarily/i.test(message) ? "overloaded" : "rate_limited";
  if (code === "402") return "billing";
  if (code === "401") return "auth";
  if (code === "403") return "invalid_request";
  if (code === "408") return "timeout";
  if (code === "502") return "server_error";
  if (code === "503") return "overloaded";
  return code ? openaiErrorTypes[code] : undefined;
}

function readOutputContract(requestBody: string): OutputContract {
  const body = jsonObject(requestBody);
  const format = isRecord(body.response_format) ? body.response_format : {};
  const jsonSchema = isRecord(format.json_schema) ? format.json_schema : {};
  const tools = new Map<string, DeclaredTool>();
  for (const tool of records(body.tools)) {
    const fn = isRecord(tool.function) ? tool.function : {};
    const name = nonEmptyString(fn.name);
    if (name) tools.set(name, { json: true, schema: fn.parameters });
  }
  return {
    tools,
    openTools: false,
    json: format.type === "json_schema" || format.type === "json_object",
    responseSchema: format.type === "json_schema" ? jsonSchema.schema : undefined,
  };
}

/** Chat completions report usage on the final chunk, as `prompt_tokens` and `completion_tokens`. */
function readUsage(body: string, requestBody: string): ModelUsage | undefined {
  for (const value of jsonObjects(body).reverse()) {
    if (!isRecord(value.usage)) continue;
    const usage = value.usage;
    const inputTokens = finiteNumber(usage.prompt_tokens);
    const outputTokens = finiteNumber(usage.completion_tokens);
    if (inputTokens === undefined && outputTokens === undefined) continue;
    const prompt = isRecord(usage.prompt_tokens_details) ? usage.prompt_tokens_details : {};
    const completion = isRecord(usage.completion_tokens_details)
      ? usage.completion_tokens_details
      : {};
    const written = finiteNumber(prompt.cache_write_tokens) ?? 0;
    const cost = finiteNumber(usage.cost);
    const costDetails = isRecord(usage.cost_details) ? usage.cost_details : {};
    const promptCost = finiteNumber(costDetails.upstream_inference_prompt_cost);
    const completionCost = finiteNumber(costDetails.upstream_inference_completions_cost);
    return {
      model: nonEmptyString(value.model),
      inputTokens: inputTokens ?? 0,
      audioInputTokens: finiteNumber(prompt.audio_tokens) ?? 0,
      implicitCacheReadTokens: finiteNumber(prompt.cached_tokens) ?? 0,
      explicitCacheReadTokens: 0,
      cacheWrites: requestedCacheWrites(written, requestBody),
      outputTokens: outputTokens ?? 0,
      reasoningTokens: finiteNumber(completion.reasoning_tokens) ?? 0,
      toolUses: {},
      serviceTier: nonEmptyString(value.service_tier),
      ...(cost === undefined ? {} : { reportedCostUsd: cost }),
      ...(promptCost === undefined ? {} : { reportedPromptCostUsd: promptCost }),
      ...(completionCost === undefined ? {} : { reportedCompletionCostUsd: completionCost }),
    };
  }
}

function readSettings(requestBody: string): ModelRequestSettings {
  const body = jsonObject(requestBody);
  const reasoning = isRecord(body.reasoning) ? body.reasoning : {};
  const responseFormat = isRecord(body.response_format) ? body.response_format : {};
  return {
    model: nonEmptyString(body.model),
    maxTokens: finiteNumber(body.max_tokens) ?? finiteNumber(body.max_completion_tokens),
    temperature: finiteNumber(body.temperature),
    topP: finiteNumber(body.top_p),
    stream: body.stream === true,
    reasoningLevel: nonEmptyString(reasoning.effort) ?? nonEmptyString(body.reasoning_effort),
    outputType: outputType(nonEmptyString(responseFormat.type)),
    promptCacheKey: nonEmptyString(body.prompt_cache_key),
  };
}

/** The system messages a request opens with, its tools, then every other message in order. */
function readPrompt(requestBody: string): ModelPrompt {
  const body = jsonObject(requestBody);
  const system: PromptPart[] = [];
  const conversation: PromptPart[] = [];
  const toolResults = new Map<string, string>();
  for (const message of records(body.messages)) {
    const role = nonEmptyString(message.role);
    const content = contentText(message.content);
    if (role === "system" || role === "developer") {
      const part: PromptPart = { role: "system", label: "System", text: content, sent: content };
      (conversation.length ? conversation : system).push(part);
    } else if (role === "tool") {
      const id = nonEmptyString(message.tool_call_id);
      if (id) toolResults.set(id, content);
      conversation.push({
        role: "tool",
        label: "Tool result",
        text: readableText(content),
        sent: content,
      });
    } else if (role === "assistant") {
      const reasoning = nonEmptyString(message.reasoning);
      if (reasoning)
        conversation.push({
          role: "assistant",
          label: "Reasoning",
          text: reasoning,
          sent: reasoning,
        });
      else {
        const encrypted = records(message.reasoning_details).filter(
          detail => detail.type === "reasoning.encrypted",
        );
        if (encrypted.length)
          conversation.push(
            encryptedReasoningPart(encrypted.map(detail => argumentsText(detail.data)).join("")),
          );
      }
      if (content)
        conversation.push({ role: "assistant", label: "Assistant", text: content, sent: content });
      for (const call of records(message.tool_calls)) {
        const fn = isRecord(call.function) ? call.function : {};
        conversation.push({
          role: "assistant",
          label: `Calls ${nonEmptyString(fn.name) ?? "a tool"}`,
          text: readableText(fn.arguments),
          sent: argumentsText(fn.arguments),
        });
      }
    } else {
      conversation.push({ role: "user", label: "User", text: content, sent: content });
    }
  }
  const tools = records(body.tools).map(tool =>
    toolPart(isRecord(tool.function) ? tool.function : tool),
  );
  return { parts: [...system, ...tools, ...conversation], toolResults };
}

interface ChatChoice {
  text: string;
  reasoning: string;
  toolCalls: { id?: string; name: string; arguments: string }[];
  finishReason?: string;
  nativeFinishReason?: string;
}

/** Reads a whole completion or its streamed chunks, choice by choice. */
function readReply(responseBody: string): ModelReply {
  const choices = new Map<number, ChatChoice>();
  const reply: ModelReply = {
    reasoning: "",
    reasoningEncryptedWithoutSummary: false,
    text: "",
    toolCalls: [],
    finishReasons: [],
    nativeFinishReasons: [],
  };
  let encryptedReasoning = false;
  for (const event of jsonObjects(responseBody)) {
    encryptedReasoning ||= records(event.choices).some(choice => {
      const source = isRecord(choice.delta)
        ? choice.delta
        : isRecord(choice.message)
          ? choice.message
          : {};
      return records(source.reasoning_details).some(
        detail => detail.type === "reasoning.encrypted",
      );
    });
    reply.id = nonEmptyString(event.id) ?? reply.id;
    reply.model = nonEmptyString(event.model) ?? reply.model;
    reply.upstreamProvider = nonEmptyString(event.provider) ?? reply.upstreamProvider;
    reply.serviceTier = nonEmptyString(event.service_tier) ?? reply.serviceTier;
    if (isRecord(event.error)) {
      reply.errorType = errorCode(event.error);
      reply.errorMessage = nonEmptyString(event.error.message);
    }
    if (isRecord(event.usage)) reply.upstreamCostUsd = upstreamCost(event.usage);
    for (const [position, choice] of records(event.choices).entries()) {
      const index = finiteNumber(choice.index) ?? position;
      const accumulated = choices.get(index) ?? { text: "", reasoning: "", toolCalls: [] };
      choices.set(index, accumulated);
      accumulateChoice(accumulated, choice);
    }
  }
  const ordered = [...choices.entries()]
    .sort(([left], [right]) => left - right)
    .map(([, choice]) => choice);
  reply.reasoning = ordered.map(choice => choice.reasoning).join("");
  reply.reasoningEncryptedWithoutSummary = encryptedReasoning && !reply.reasoning;
  reply.text = ordered.map(choice => choice.text).join("");
  reply.toolCalls = ordered.flatMap(choice =>
    choice.toolCalls
      .filter(Boolean)
      .map(call => ({ ...call, arguments: parseArguments(call.arguments) })),
  );
  reply.finishReasons = ordered.flatMap(choice =>
    choice.finishReason ? [finishReason(choice.finishReason)] : [],
  );
  reply.nativeFinishReasons = ordered.flatMap(choice =>
    choice.nativeFinishReason ? [choice.nativeFinishReason] : [],
  );
  return reply;
}

/** Folds one choice into the running total: a whole message, or one streamed delta of it. */
function accumulateChoice(accumulated: ChatChoice, choice: Record<string, unknown>): void {
  const source = isRecord(choice.message)
    ? choice.message
    : isRecord(choice.delta)
      ? choice.delta
      : {};
  accumulated.text += contentText(source.content);
  accumulated.reasoning += nonEmptyString(source.reasoning) ?? "";
  for (const [position, call] of records(source.tool_calls).entries()) {
    const index = finiteNumber(call.index) ?? position;
    const fn = isRecord(call.function) ? call.function : {};
    const existing = accumulated.toolCalls[index] ?? { name: "", arguments: "" };
    accumulated.toolCalls[index] = {
      id: nonEmptyString(call.id) ?? existing.id,
      name: existing.name + (nonEmptyString(fn.name) ?? ""),
      arguments: existing.arguments + argumentsText(fn.arguments),
    };
  }
  accumulated.finishReason = nonEmptyString(choice.finish_reason) ?? accumulated.finishReason;
  accumulated.nativeFinishReason =
    nonEmptyString(choice.native_finish_reason) ?? accumulated.nativeFinishReason;
}

function readChunks(responseBody: string): StreamChunk[] {
  const toolNames = new Map<number, string>();
  return jsonObjects(responseBody).map((event): StreamChunk => {
    if (isRecord(event.usage)) return { kind: "usage" };
    const choice = records(event.choices)[0] ?? {};
    const delta = isRecord(choice.delta)
      ? choice.delta
      : isRecord(choice.message)
        ? choice.message
        : {};
    const toolCall = records(delta.tool_calls)[0];
    if (toolCall) {
      const index = finiteNumber(toolCall.index) ?? 0;
      const fn = isRecord(toolCall.function) ? toolCall.function : {};
      const name = toolNames.get(index) ?? nonEmptyString(fn.name) ?? "a tool";
      toolNames.set(index, name);
      return {
        kind: "tool",
        tool: name,
        call: String(index),
        text: typeof fn.arguments === "string" ? fn.arguments : "",
      };
    }
    const details = records(delta.reasoning_details);
    if (nonEmptyString(delta.reasoning) || details.length)
      return {
        kind: "reasoning",
        text:
          nonEmptyString(delta.reasoning) ??
          details
            .map(detail => nonEmptyString(detail.text) ?? nonEmptyString(detail.summary) ?? "")
            .join(""),
      };
    const text = contentText(delta.content);
    if (text) return { kind: "text", text };
    if (nonEmptyString(choice.finish_reason)) return { kind: "finish" };
    return { kind: "other" };
  });
}

function finishReason(reason: string): string {
  if (reason === "tool_calls" || reason === "function_call") return "tool_call";
  return reason;
}
